import { CommonModule } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { Subject, takeUntil } from "rxjs";

import { JslibModule } from "@bitwarden/angular/jslib.module";
import {
  InputPasswordComponent,
  InputPasswordFlow,
  PasswordInputResult,
  RegistrationFinishService,
} from "@bitwarden/auth/angular";
import {
  LoginEmailServiceAbstraction,
  LoginStrategyServiceAbstraction,
  LoginSuccessHandlerService,
  PasswordLoginCredentials,
} from "@bitwarden/auth/common";
import { AccountApiService } from "@bitwarden/common/auth/abstractions/account-api.service";
import { RegisterSendVerificationEmailRequest } from "@bitwarden/common/auth/models/request/registration/register-send-verification-email.request";
import { I18nService } from "@bitwarden/common/platform/abstractions/i18n.service";
import { LogService } from "@bitwarden/common/platform/abstractions/log.service";
import { ValidationService } from "@bitwarden/common/platform/abstractions/validation.service";
import {
  ButtonModule,
  CheckboxModule,
  FormFieldModule,
  LinkModule,
  ToastService,
} from "@bitwarden/components";

// FIXME(https://bitwarden.atlassian.net/browse/CL-764): Migrate to OnPush
// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  templateUrl: "./web-registration.component.html",
  styles: [
    `
      ::ng-deep auth-input-password button[type="submit"]:not(:disabled) {
        background-color: rgb(96, 57, 187) !important;
        border-color: rgb(96, 57, 187) !important;
      }

      ::ng-deep auth-input-password button[type="submit"]:not(:disabled):hover,
      ::ng-deep auth-input-password button[type="submit"]:not(:disabled):focus-visible {
        background-color: rgb(79, 47, 156) !important;
        border-color: rgb(79, 47, 156) !important;
      }
    `,
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    JslibModule,
    FormFieldModule,
    CheckboxModule,
    LinkModule,
    ButtonModule,
    InputPasswordComponent,
    RouterLink,
  ],
})
export class WebRegistrationComponent implements OnInit, OnDestroy {
  inputPasswordFlow = InputPasswordFlow.SetInitialPasswordAccountRegistration;
  submitting = false;
  emailReadonly = false;

  formGroup = this.formBuilder.group({
    email: ["", [Validators.required, Validators.email]],
    name: ["", [Validators.required]],
    acceptTerms: [false, Validators.requiredTrue],
  });

  private destroy$ = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private accountApiService: AccountApiService,
    private registrationFinishService: RegistrationFinishService,
    private loginStrategyService: LoginStrategyServiceAbstraction,
    private loginSuccessHandlerService: LoginSuccessHandlerService,
    private router: Router,
    private toastService: ToastService,
    private i18nService: I18nService,
    private validationService: ValidationService,
    private logService: LogService,
    private loginEmailService: LoginEmailServiceAbstraction,
  ) {}

  get emailValue(): string {
    return this.formGroup.controls.email.value ?? "";
  }

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((qParams) => {
      if (qParams.email != null && qParams.email.indexOf("@") > -1) {
        this.formGroup.controls.email.setValue(qParams.email);
        this.emailReadonly = qParams.emailReadonly === "true";
      }
    });

    this.loginEmailService.loginEmail$.pipe(takeUntil(this.destroy$)).subscribe((email) => {
      if (email) {
        this.formGroup.patchValue({ email });
      }
    });
  }

  async handlePasswordSubmit(passwordInputResult: PasswordInputResult): Promise<void> {
    this.formGroup.markAllAsTouched();
    if (this.formGroup.invalid) {
      if (this.formGroup.controls.acceptTerms.invalid) {
        this.toastService.showToast({
          variant: "error",
          title: null,
          message: this.i18nService.t("bvAcceptTermsRequired"),
        });
      }
      return;
    }

    const email = this.formGroup.controls.email.value;
    if (!email) {
      return;
    }

    this.submitting = true;

    try {
      const sanitizedName =
        this.formGroup.controls.name.value === "" ? null : this.formGroup.controls.name.value;

      const request = new RegisterSendVerificationEmailRequest(email, sanitizedName, false);
      const verificationResult =
        await this.accountApiService.registerSendVerificationEmail(request);

      if (typeof verificationResult !== "string") {
        this.toastService.showToast({
          variant: "info",
          title: null,
          message: this.i18nService.t("followTheLinkInTheEmailSentTo") + " " + email,
        });
        return;
      }

      await this.registrationFinishService.finishRegistration(
        email,
        passwordInputResult,
        verificationResult,
      );

      this.toastService.showToast({
        variant: "success",
        title: null,
        message: this.i18nService.t("newAccountCreated2"),
      });

      const credentials = new PasswordLoginCredentials(email, passwordInputResult.newPassword);
      const authenticationResult = await this.loginStrategyService.logIn(credentials);

      if (authenticationResult?.requiresTwoFactor) {
        await this.router.navigate(["/2fa"]);
        return;
      }

      await this.loginSuccessHandlerService.run(
        authenticationResult.userId,
        authenticationResult.masterPassword ?? null,
      );

      await this.router.navigate(["/vault"]);
    } catch (error) {
      this.validationService.showError(error);
      this.logService.error(error);
    } finally {
      this.submitting = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
